import React from "react";
import ConfirmationModal from "../ConfirmationModal.jsx";
import GeotabMap from "../geotab/GeotabMap.jsx";
import { fetchConsolidationRouteTrace } from "../../api/geotabApi.js";
import { fmtTime } from "../geotab/geotabFormat.js";

const km = value => value == null ? "Inconnus" : `${Math.round(value).toLocaleString("fr-CA")} km`;
const signedKm = value => `${Math.round(value) === 0 ? "" : value > 0 ? "+" : "−"}${km(Math.abs(value))}`;

export default function RouteComparisonDialog({ routes, initialId, date, onClose }) {
  const [selected, setSelected] = React.useState(initialId);
  const [state, setState] = React.useState({ points: [], loading: true, error: "" });
  const [showPlan, setShowPlan] = React.useState(true);
  const [showReal, setShowReal] = React.useState(true);
  const [attempt, setAttempt] = React.useState(0);
  const route = routes.find(r => r.id === selected) || routes[0];
  const traceKey = JSON.stringify(route?.trace);
  const requestKey = `${date}|${traceKey}`;
  React.useEffect(() => {
    const controller = new AbortController();
    setState({ key: requestKey, points: [], loading: Boolean(route?.trace), error: "" });
    if (route?.trace) fetchConsolidationRouteTrace(date, route.trace, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setState({ key: requestKey, points: data.points || [], loading: false, error: "" }); })
      .catch(error => { if (!controller.signal.aborted) setState({ key: requestKey, points: [], loading: false,
        // Texte fixe ; seule l'absence de configuration se dit autrement (le détail technique reste dans les journaux du serveur).
        error: error.code === "geotab_not_configured" ? error.message : "Impossible de charger la trace Geotab." }); });
    return () => controller.abort();
  }, [date, traceKey, attempt]);
  if (!route) return null;
  const points = [route.from, route.to].flatMap((s, i) => Number.isFinite(s.lat) && Number.isFinite(s.lon)
    ? [{ ...s, label: i ? "B" : "A", title: `${i ? "Arrivée" : "Départ"} : ${s.name}` }] : []);
  const realPoints = state.key === requestKey ? state.points : [];
  const layers = { plannedLine: showPlan ? route.plannedLine : [], realLine: showReal ? realPoints : [], sites: points };
  return <ConfirmationModal title="Comparer les trajets" eyebrow="Consolidation · carte" tone="neutral" panelSize="wide"
    cancelLabel="" confirmLabel="Fermer" onCancel={onClose} onConfirm={onClose}>
    <div className="min-w-0 space-y-2" data-route-comparison>
      <label className="block text-xs font-semibold text-slate-700">Trajet
        <select value={route.id} onChange={event => setSelected(Number(event.target.value))}
          className="mt-1 block h-7 w-full max-w-full rounded-[4px] border border-slate-300 bg-white py-0 pl-2 pr-7 text-xs text-slate-900 focus-visible:outline-accent">
          {routes.map(r => <option key={r.id} value={r.id}>{r.name}{r.gap != null ? ` · ${signedKm(r.gap)}` : ""}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-3 gap-1 text-xs tabular-nums">
        {[["TransIA prévu", route.plannedKm], ["Geotab réel", route.realKm], ["Écart", route.gap]].map(([title, value], i) =>
          <div key={title} className="rounded-[4px] border border-slate-300 bg-white px-2 py-1">
            <span className="block text-[11px] text-slate-600">{title}</span>
            <strong className="whitespace-nowrap text-sm text-slate-900">{i === 2 && value != null ? signedKm(value) : km(value)}</strong>
          </div>)}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-slate-700">
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showPlan} onChange={e => setShowPlan(e.target.checked)} />
          <span className="w-6 border-t-[3px] border-dashed border-accent" />Prévu TransIA</label>
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showReal} onChange={e => setShowReal(e.target.checked)} />
          <span className="w-6 border-t-[3px] border-slate-900" />Réel Geotab</label>
        {route.trace ? <span className="font-normal">Geotab : {fmtTime(route.trace.fromMin)}–{fmtTime(route.trace.toMin)}</span> : null}
      </div>
      {state.loading ? <p role="status" className="m-0 text-xs text-slate-600">Chargement de la trace Geotab…</p> : null}
      {state.error ? <p role="alert" className="m-0 rounded-[4px] border border-red-600 bg-white p-2 text-xs text-red-700">{state.error}
        <button type="button" onClick={() => setAttempt(value => value + 1)}
          className="ml-2 rounded-[4px] border border-slate-300 bg-white px-2 py-0.5 text-slate-700 hover:bg-slate-50">Réessayer</button></p> : null}
      {!route.trace ? <p className="m-0 text-xs text-slate-600">{route.reason}</p> : null}
      {!route.plannedLine.length ? <p className="m-0 text-xs text-slate-600">Le tracé prévu n’a pas été conservé pour ce trajet.</p> : null}
      {!state.loading && !state.error && route.trace && state.points.length < 2 ? <p className="m-0 text-xs text-slate-600">Aucune trace GPS disponible sur cet intervalle.</p> : null}
      <GeotabMap layers={layers} fitKey={`${route.id}-${realPoints.length}-${showPlan}-${showReal}`} fitOnResize className="h-[min(48vh,440px)] min-h-[220px]" />
      <p className="m-0 text-[11px] text-slate-600">A : {route.from.name} · B : {route.to.name}. Décochez un tracé pour mieux voir l’autre.</p>
    </div>
  </ConfirmationModal>;
}
